// File System Access API types not in the default TS lib.

interface ShowDirectoryPickerOptions {
  mode?: "read" | "readwrite";

  id?: string;

  startIn?: string;
}

type FileSystemDirectoryHandleWithValues = FileSystemDirectoryHandle & {
  values(): AsyncIterableIterator<
    FileSystemDirectoryHandle | FileSystemFileHandle
  >;
};

declare global {
  interface Window {
    showDirectoryPicker(
      options?: ShowDirectoryPickerOptions,
    ): Promise<FileSystemDirectoryHandle>;
  }
}

const getErrorName = (err: unknown): string | undefined =>
  err && typeof err === "object" && "name" in err
    ? String((err as { name?: unknown }).name)
    : undefined;

class DirHandler {
  handler: FileSystemDirectoryHandle | null;

  rootName: string | null;

  constructor() {
    this.handler = null;

    this.rootName = null;
  }

  async openDir() {
    try {
      const dirHandler = await window.showDirectoryPicker({
        mode: "readwrite",

        id: "radioStation",

        startIn: "music",
      });

      this.handler = dirHandler;

      this.rootName = await this.getRootDirName();

      return this;
    } catch (error) {
      console.error("Error opening directory:", error);

      throw error;
    }
  }

  async readFiles(directory = "/artists/") {
    try {
      const dirHandler = (await this.getDirHandle(
        directory,
      )) as FileSystemDirectoryHandleWithValues;

      const entries = dirHandler.values();

      const files = [];

      for await (const entry of entries) {
        files.push(entry);
      }

      return files;
    } catch (error) {
      console.error("Error reading directory:", error);

      throw error;
    }
  }

  async getRootDirName() {
    try {
      if (!this.handler) {
        throw new Error("No directory selected. Use openDir() first.");
      }

      return this.handler.name;
    } catch (error) {
      console.error("Error getting root directory name:", error);

      throw error;
    }
  }

  async readFile(
    outputPath: string,

    filename: string,

    recursive = true,
  ): Promise<{ name: string; content: Blob }> {
    if (!this.handler) {
      window.location.href = "/install";

      throw new Error("No directory selected. Use openDir() first.");
    }

    const fileHandle = await this.getFileHandle(
      outputPath,
      filename,
      recursive,
    );

    const file = await fileHandle.getFile();

    const content = await file.arrayBuffer();

    return {
      name: filename,

      content: new Blob([content], { type: file.type }),
    };
  }

  async getDirHandle(
    route: string,
    recursive = false,
  ): Promise<FileSystemDirectoryHandle> {
    try {
      if (!this.handler) {
        throw new Error("Root directory handle is not available.");
      }

      const parts = route.split("/").filter((part) => part !== "");

      let currentDirHandler = this.handler;

      for (const part of parts) {
        try {
          currentDirHandler = await currentDirHandler.getDirectoryHandle(part, {
            create: false,
          });
        } catch (err) {
          console.error(`Error opening folder ${part}:`, err);

          if (getErrorName(err) === "NotFoundError") {
            if (recursive) {
              currentDirHandler = await currentDirHandler.getDirectoryHandle(
                part,
                {
                  create: true,
                },
              );
            } else {
              throw err;
            }
          } else {
            throw err;
          }
        }
      }

      return currentDirHandler;
    } catch (error) {
      console.error("Error getting directory handle:", error);

      throw error;
    }
  }

  async getFileHandle(
    filePath: string,

    filename: string,

    recursive = false,
  ): Promise<FileSystemFileHandle> {
    const dirHandler = await this.getDirHandle(filePath, recursive);

    const fileHandle = await dirHandler.getFileHandle(filename);

    return fileHandle;
  }

  async createFile(
    output: string,

    fileName: string,

    content: FileSystemWriteChunkType,

    recursive = true,
  ): Promise<void> {
    try {
      const dirHandler = await this.getDirHandle(output, recursive);

      if (recursive) {
        const fileHandle = await dirHandler.getFileHandle(fileName, {
          create: true,
        });

        await this.writeFile(fileHandle, content);
      } else {
        try {
          const fileHandle = await dirHandler.getFileHandle(output, {
            create: false,
          });

          await this.writeFile(fileHandle, content);
        } catch (err) {
          if (getErrorName(err) === "NotFoundError") {
            const handler = (await dirHandler.getFileHandle(output, {
              create: true,
            })) as unknown as FileSystemDirectoryHandle;

            const fileHandle = await handler.getFileHandle(fileName);

            await this.writeFile(fileHandle, content);
          } else {
            throw err;
          }
        }
      }
    } catch (error) {
      console.error("Error creating file:", error);

      throw error;
    }
  }

  async writeFile(
    fileHandle: FileSystemFileHandle,

    content: FileSystemWriteChunkType,
  ): Promise<void> {
    const writable = await fileHandle.createWritable();

    if (
      typeof content === "string" ||
      content instanceof Blob ||
      content instanceof ArrayBuffer ||
      ArrayBuffer.isView(content)
    ) {
      await writable.write(content);
    } else if (typeof content === "object" && content.data) {
      await writable.write(content);
    } else {
      throw new TypeError("Provided content is not of a supported type.");
    }

    await writable.close();
  }
}

export default new DirHandler();
